import { useState, useEffect } from 'react';
import { Form } from 'antd';
import { categoryService } from '../../Services/CategoriesService';
import { toast } from 'react-toastify';

export const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [error, setError] = useState(null);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
    orderByLatest: true,
    onlyWithSubCategories: false
  });

  // Fetch categories with details - Updated for pagination
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const data = await categoryService.getCategoriesWithDetails({
        pageNumber: pagination.currentPage,
        pageSize: pagination.pageSize,
        orderByLatest: pagination.orderByLatest,
        onlyWithSubCategories: pagination.onlyWithSubCategories
      });
      console.log("categories list.........", data);

      // Set categories from API response
      setCategories(Array.isArray(data?.items) ? data.items : []);

      setPagination(prev => ({
  ...prev,
  totalCount: data?.totalCount ?? 0,
  totalPages: data?.totalPages ?? 0,
  currentPage: data?.pageNumber ?? prev.currentPage,
  pageSize: data?.pageSize ?? prev.pageSize,
  orderByLatest: data?.orderByLatest ?? prev.orderByLatest
}));


      setError(null);
    } catch (error) {
      const errorMessage = error?.response?.data?.message || "Failed to fetch categories";
      toast.error(errorMessage);
      setError(errorMessage);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch category details for update - Same as before
  const fetchCategoryForUpdate = async (categoryId) => {
    setLoadingSubCategories(true);
    try {
      const data = await categoryService.getCategoryForUpdate(categoryId);
      return data;
    } catch (error) {
      console.error('Error fetching category for update:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to fetch category details';
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoadingSubCategories(false);
    }
  };

  const addCategory = async (categoryData) => {
  try {
    console.log("............paylod",categoryData);
    await categoryService.addCategory(categoryData); // OBJECT
    toast.success('Category added successfully');

    await fetchCategories();

    setModalVisible(false);
    form.resetFields();
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message || 'Failed to add category';
    toast.error(errorMessage);
    throw error;
  }
};


  // Update subcategories - Same as before
  const updateSubCategories = async (categoryId, subCategoriesData) => {
    setLoadingSubCategories(true);
    try {
      await categoryService.addSubCategories(subCategoriesData);
      toast.success('Subcategories Added/updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating subcategories:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to update subcategories';
      toast.error(errorMessage);
      throw error;
    } finally {
      setLoadingSubCategories(false);
    }
  };

  // Edit category - Same as before
  const editCategory = async (categoryData) => {
    try {
      await categoryService.editCategory(categoryData.id, categoryData);
      toast.success('Category updated successfully');
      
      // Refresh categories after editing
      await fetchCategories();
      
      setModalVisible(false);
      setEditingCategory(null);
      form.resetFields();
      return true;
    } catch (error) {
      console.error('Error editing category:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to update category';
      toast.error(errorMessage);
      throw error;
    }
  };
const handleSubmit = async (values) => {
  try {
    if (editingCategory) {
      await editCategory({ ...values, id: editingCategory.id });
    } else {
      await addCategory(values); // ✅ OBJECT
    }
  } catch (error) {
    console.error('Form submission error:', error);
  }
};


  // Handle edit - Same as before
  const handleEdit = (category) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name: category.name,
      description: category.description
    });
    setModalVisible(true);
  };

  // Handle modal close - Same as before
  const handleModalClose = () => {
    setModalVisible(false);
    setEditingCategory(null);
    form.resetFields();
  };

  // Handle add new - Same as before
  const handleAddNew = () => {
    setEditingCategory(null);
    form.resetFields();
    setModalVisible(true);
  };

  // Handle pagination change - FIXED: Should handle both page and size
  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      pageSize: pageSize
    }));
  };

  // Handle page size change separately if needed
  const handlePageSizeChange = (current, size) => {
    setPagination(prev => ({
      ...prev,
      currentPage: 1, // Reset to first page when size changes
      pageSize: size
    }));
  };

  // Handle sort change
  const handleSortChange = (orderByLatest) => {
    setPagination(prev => ({
      ...prev,
      orderByLatest: orderByLatest,
      currentPage: 1 // Reset to first page when sort changes
    }));
  };

  // Handle filter change (onlyWithSubCategories)
  const handleFilterChange = (onlyWithSubCategories) => {
    setPagination(prev => ({
      ...prev,
      onlyWithSubCategories: onlyWithSubCategories,
      currentPage: 1 // Reset to first page when filter changes
    }));
  };

  // Fetch data when pagination changes
  useEffect(() => {
    fetchCategories();
  }, [
    pagination.currentPage,
    pagination.pageSize,
    pagination.orderByLatest,
    pagination.onlyWithSubCategories
  ]);

  return {
    categories,
    error,
    loading,
    modalVisible,
    editingCategory,
    form,
    loadingSubCategories,
    pagination,
    fetchCategories,
    fetchCategoryForUpdate,
    updateSubCategories,
    handleSubmit,
    handleEdit,
    handleModalClose,
    handleAddNew,
    handlePageChange,
    handlePageSizeChange,
    handleSortChange,
    handleFilterChange
  };
};